import React, { useEffect } from 'react'
import { ArrowRight } from 'lucide-react'
import Gallery from '../component/gallery'
import Reveal from '../component/Reveal'
import Atmosphere from '../component/Atmosphere'
import YouTubeVideoWithThumbnail from '../component/FacadeVideo'

const team = [
  { image: '/Nicolas.svg', titre: 'Nicolas Pierret', text: 'Directeur du développement' },
  { image: '/Alice.jpeg', titre: 'Alice Rosier', text: 'Professeure en design graphique' },
  { image: '/Alexandre.jpeg', titre: 'Alexandre Borikic', text: 'Professeur de modélisation 3D' },
  { image: '/Jean-Baptiste-Guerin.jpg', titre: 'Jean-Baptiste Guerin', text: 'Développeur web' },
  { image: '/Sylvie-Sanchez-fondatrice.jpeg', titre: 'Sylvie Sanchez', text: 'Experte projet accessibilité' },
]

const presentation = ['/presentation1-4.png', '/presentation2-4.png', '/presentation3-4.png', '/presentation4-4.png']

function QuiSommeNous() {
  useEffect(() => {
    window.scrollTo(0, 0)
    document.title = 'Qui sommes-nous ? — Place des Victoires'
  }, [])

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-10 lg:pt-44 lg:pb-16">
        <Atmosphere variant="top" />
        <div className="container-pdv relative z-10">
          <Reveal className="max-w-3xl">
            <span className="eyebrow">L'association &amp; le studio</span>
            <h1 className="mt-5 font-display font-extrabold leading-[1] text-[clamp(2.4rem,6vw,4.5rem)]">
              Qui sommes-<span className="text-gradient">nous&nbsp;?</span>
            </h1>
            <p className="mt-6 text-lg text-[#b6a9d8]">
              Une équipe de talents atypiques portée par une mission : l'inclusion par la création
              numérique.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Bloc 1 — Le studio & Creative Handicap */}
      <section className="relative overflow-hidden py-16 lg:py-24">
        <Atmosphere variant="calm" />
        <div className="container-pdv relative z-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <span className="eyebrow">Le studio</span>
            <h2 className="mt-4 font-display font-extrabold leading-[1.08] text-[clamp(1.7rem,3.2vw,2.6rem)]">
              Un cercle vertueux : <span className="text-gradient">formation → autonomie → emploi</span>
            </h2>
            <div className="mt-6 space-y-4 text-lg text-[#c4b8e6] leading-relaxed">
              <p>
                <strong className="text-white">Place des Victoires</strong> est le studio de création
                multimédia en entreprise adaptée de <strong className="text-white">Creative Handicap</strong>,
                association loi 1901 à but non lucratif, fondée en 2004 et reconnue d'intérêt général.
              </p>
              <p>
                Son objet : <strong className="text-white">l'inclusion socio-professionnelle des personnes
                en situation de handicap</strong> ou éloignées de l'emploi, par la création artistique et
                numérique. Creative Handicap a créé la première formation professionnelle dédiée à ces
                publics sur les métiers de la création numérique : accessibilité numérique, design
                graphique, animation 2D/3D.
              </p>
            </div>
            <div className="mt-7 inline-flex flex-wrap items-center gap-3 font-display font-bold text-white">
              <span className="rounded-full bg-grad-violet px-4 py-1.5 shadow-glow">Formation</span>
              <ArrowRight size={20} className="text-violet-bright" aria-hidden="true" />
              <span className="rounded-full bg-grad-violet px-4 py-1.5 shadow-glow">Autonomie</span>
              <ArrowRight size={20} className="text-violet-bright" aria-hidden="true" />
              <span className="rounded-full bg-grad-violet px-4 py-1.5 shadow-glow">Emploi</span>
            </div>
          </Reveal>

          <Reveal delay={120} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {presentation.map((src, i) => (
              <div key={i} className="overflow-hidden rounded-2xl card-surface ring-gradient">
                <img src={src} alt="" aria-hidden="true" className="h-44 sm:h-56 w-full object-cover transition-transform duration-700 hover:scale-110" />
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Bloc 2 — Formation CIARA */}
      <section className="relative overflow-hidden py-16 lg:py-24 border-t border-white/5">
        <div className="container-pdv grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:order-2">
            <span className="eyebrow">La formation</span>
            <h2 className="mt-4 font-display font-extrabold leading-[1.08] text-[clamp(1.7rem,3.2vw,2.6rem)]">
              La formation <span className="text-gradient">CIARA</span>
            </h2>
            <div className="mt-6 space-y-4 text-lg text-[#c4b8e6] leading-relaxed">
              <p>
                CIARA est une <strong className="text-white">formation professionnelle</strong> à
                destination des jeunes en situation de handicap et des décrocheurs scolaires de 18 à 30 ans,
                sur les métiers de la création numérique : accessibilité numérique, design graphique,
                animation 2D/3D, développement web.
              </p>
              <p>
                Elle se déroule sur deux ans : une année préparatoire puis une année d'apprentissage en
                entreprise.
              </p>
            </div>
            <ul className="mt-6 flex flex-wrap gap-2.5">
              {['RGAA', 'HTML', 'Blender', 'Suite Adobe'].map((t) => (
                <li key={t} className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-sm text-[#cbbef0]">{t}</li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={120} className="lg:order-1">
            <div className="card-surface ring-gradient rounded-2xl overflow-hidden">
              <div className="relative w-full bg-[#07021a]" style={{ paddingTop: '56.25%' }}>
                <div className="absolute inset-0">
                  <YouTubeVideoWithThumbnail videoId="NPImWyS5b2M" title="La formation CIARA de Creative Handicap" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bloc 3 — Nos valeurs */}
      <section className="relative overflow-hidden py-16 lg:py-24 border-t border-white/5">
        <Atmosphere variant="default" />
        <div className="container-pdv relative z-10">
          <Reveal className="card-surface ring-gradient rounded-3xl p-8 sm:p-12 max-w-4xl mx-auto">
            <span className="eyebrow">Nos valeurs</span>
            <h2 className="mt-4 font-display font-extrabold leading-[1.08] text-[clamp(1.7rem,3.2vw,2.6rem)]">
              Révéler les <span className="text-gradient">« <span lang="en">Mad Skills</span> »</span>
            </h2>
            <div className="mt-6 space-y-4 text-lg text-[#c4b8e6] leading-relaxed">
              <p>
                Le plein emploi des personnes en situation de handicap (PSH) est loin d'être atteint. Les
                entreprises de plus de 20 salariés, tenues d'employer au moins 6 % de PSH, préfèrent souvent
                payer la taxe AGEFIPH plutôt que d'accueillir des travailleurs handicapés, faute de
                sensibilisation et de formation.
              </p>
              <p>
                Pour faciliter l'intégration de nos apprenants et prouver leurs compétences, Creative Handicap
                a créé le studio <strong className="text-white">Place des Victoires</strong>, qui recrute des
                PSH sur leurs <span lang="en">« Mad Skills »</span>* — des compétences empreintes
                d'originalité, révélatrices de traits de caractère atypiques et décalés.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bloc 4 — Nos équipes */}
      <section className="relative overflow-hidden py-16 lg:py-24 border-t border-white/5">
        <div className="container-pdv">
          <Reveal className="max-w-2xl mb-12">
            <span className="eyebrow">Nos équipes</span>
            <h2 className="mt-4 font-display font-extrabold leading-[1.08] text-[clamp(1.7rem,3.2vw,2.6rem)]">
              Des talents <span className="text-gradient">atypiques</span>
            </h2>
            <p className="mt-5 text-lg text-[#b6a9d8]">
              Des profils issus de la neurodiversité — créativité, concentration, logique, imagination,
              minutie, pensée visuelle — accompagnés de professionnels de la création numérique et de
              l'inclusion.
            </p>
          </Reveal>
          <Reveal>
            <Gallery images={team} version="prestation" />
          </Reveal>
        </div>
      </section>
    </div>
  )
}

export default QuiSommeNous
